// imports
import { useSelector, useDispatch } from 'react-redux';
import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import Prompt from './index';
import { IMAGE } from '../../assets';
import { LINKS } from '../../constants';
import { IModal } from '../../common/types';
import InfoTooltip from '../../atoms/tooltip/info';
import PrimaryButton from '../../atoms/button/primary';
import { resetRewardPrompt } from '../../repositories/store/actions';
import { selectConfig, selectUIPrompt, selectUserQuests } from '../../repositories/store/slices';

// react component
const RewardsPrompt = forwardRef((_, ref) => {
  // hooks configuration
  const dispatch = useDispatch();
  const modalRef = useRef<IModal>(null);
  const { items, texts } = useSelector(selectConfig);
  const { data: quests } = useSelector(selectUserQuests);
  const { reward: { questId, awarded } } = useSelector(selectUIPrompt);
  useImperativeHandle(ref, () => ({ show: modalRef.current?.show }));
  // private methods
  const handleContinue = (): void => {
    dispatch(resetRewardPrompt());
    modalRef.current?.hide();
  };
  const questConfig = quests[questId];
  let message = questConfig?.messageMid || '';
  if (awarded.length === questConfig?.rewards.length) {
    message = questConfig?.messageSuccess || '';
  } else if (awarded.length <= 1) {
    message = questConfig?.messageError || '';
  }
  // render
  return (
    <Prompt
      ref={modalRef}
      hideCloseButton
      className="rewards"
      title={texts.popup_quest_complete?.title}
      message={`${texts.popup_quest_complete?.body.replace('{questName}', `"${questConfig?.name || ''}"`)}\n${message}`}
      body={(
        <>
          <div className="rewards-text">Rewards:</div>
          <div className="rewards-block">
            {awarded.length ? (
              <div className="rewards-container">
                {awarded.map((reward, idx) => {
                  const { itemId, amount } = reward;
                  const key = `reward_${idx}_${itemId}`;
                  return (
                    <div key={key}>
                      <div id={`reward-${key}`} className="reward-avatar">
                        <img
                          alt="item_avatar"
                          src={`${LINKS.BACKPACK_BUCKET_URL}/${itemId}.png`}
                          onError={({ currentTarget }) => { currentTarget.src = IMAGE.ITEM_PLACEHOLDER; }}
                        />
                        <div className="quantity">{amount}</div>
                      </div>
                      <div className="rewards-name">{items[itemId].name}</div>
                      <InfoTooltip
                        title={items[itemId]?.name}
                        description={items[itemId]?.info}
                        anchorSelect={`#reward-${key}`}
                      />
                    </div>
                  );
                })}
              </div>
            ) : <div className="empty-rewards">No Rewards</div>}
          </div>
        </>
      )}
      footer={(<PrimaryButton text="Continue" className="success" onClick={handleContinue} />)}
    />
  );
});

// display name
RewardsPrompt.displayName = 'RewardsPrompt';

// exports
export default RewardsPrompt;
